import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { PublicPlayer } from '@sincronia/shared';
import { dragBus } from '../../net/dragBus.js';
import { CardBack } from './PlayingCard.js';

/**
 * Arrastos dos outros jogadores.
 *
 * Mostramos apenas uma carta virada para baixo com a cor e o nome curto de
 * quem está arrastando — o número nunca é transmitido. As posições recebidas
 * são interpoladas suavemente e escritas direto no DOM.
 */
export function RemoteDrags({
  stageRef,
  players,
  meId,
}: {
  stageRef: RefObject<HTMLElement | null>;
  players: PublicPlayer[];
  meId: string | null;
}): ReactNode {
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const nodes = useRef(new Map<string, HTMLDivElement>());
  const eased = useRef(new Map<string, { x: number; y: number }>());

  useEffect(() => {
    setActiveIds(dragBus.activePlayers());
    return dragBus.subscribe(() => setActiveIds(dragBus.activePlayers()));
  }, []);

  useEffect(() => {
    let frame = 0;
    let size = { width: 0, height: 0 };

    const measure = (): void => {
      const stage = stageRef.current;
      if (!stage) return;
      size = { width: stage.clientWidth, height: stage.clientHeight };
    };

    measure();
    window.addEventListener('resize', measure);

    const tick = (): void => {
      for (const [playerId, node] of nodes.current) {
        const target = dragBus.get(playerId);
        if (!target) continue;
        const previous = eased.current.get(playerId) ?? target;
        const next = {
          x: previous.x + (target.x - previous.x) * 0.28,
          y: previous.y + (target.y - previous.y) * 0.28,
        };
        eased.current.set(playerId, next);
        node.style.transform = `translate3d(${next.x * size.width}px, ${next.y * size.height}px, 0) translate(-50%, -50%)`;
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  }, [stageRef, activeIds.length]);

  useEffect(() => {
    for (const key of [...eased.current.keys()]) {
      if (!activeIds.includes(key)) eased.current.delete(key);
    }
  }, [activeIds]);

  const visible = activeIds.filter((playerId) => playerId !== meId);
  if (visible.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-20" aria-hidden="true">
      {visible.map((playerId) => {
        const player = players.find((item) => item.id === playerId);
        return (
          <div
            key={playerId}
            data-testid={`remote-drag-${playerId}`}
            ref={(node) => {
              if (node) nodes.current.set(playerId, node);
              else nodes.current.delete(playerId);
            }}
            className="absolute top-0 left-0 will-change-transform"
          >
            <CardBack
              size="md"
              {...(player ? { avatarKey: player.avatarKey } : {})}
              {...(player ? { label: player.name } : {})}
              className="opacity-90"
            />
          </div>
        );
      })}
    </div>
  );
}
